import { Dialog, Transition } from "@headlessui/react";
import { Fragment, useState, useEffect } from "react";
import { db } from "../../firebase/firebase-config";
import { collection, getDocs, updateDoc, deleteDoc, doc } from "firebase/firestore";
import { IconSearch } from "@tabler/icons-react";
import { useDispatch } from "react-redux";
import { setPageTitle } from "../../store/themeConfigSlice";

const UserManagement = () => {
    const [users, setUsers] = useState([]);
    const [searchQuery, setSearchQuery] = useState("");
    const [selectedRole, setSelectedRole] = useState("Semua");
    const [loading, setLoading] = useState(true);
    const [editUser, setEditUser] = useState(null);
    const [isOpen, setIsOpen] = useState(false);
    const dispatch = useDispatch();

    useEffect(() => {
        dispatch(setPageTitle("User Management"));
    }, [dispatch]);

    useEffect(() => {
        const fetchUsers = async () => {
            setLoading(true);
            const querySnapshot = await getDocs(collection(db, "users"));
            const userList = querySnapshot.docs.map((doc, index) => ({
                id: doc.id,
                index: index + 1,
                ...doc.data(),
                createdAt: doc.data().createdAt?.toDate().toLocaleString() || "N/A",
            }));
            setUsers(userList);
            setLoading(false);
        };
        fetchUsers();
    }, []);

    const handleEditRole = async () => {
        if (editUser) {
            await updateDoc(doc(db, "users", editUser.id), { role: editUser.role });
            setUsers(users.map((user) => (user.id === editUser.id ? editUser : user)));
            setIsOpen(false);
        }
    };

    const handleDeleteUser = async (userId) => {
        if (confirm("Apakah Anda yakin ingin menghapus pengguna ini?")) {
            await deleteDoc(doc(db, "users", userId));
            setUsers(users.filter((user) => user.id !== userId));
        }
    };

    const openModal = (user) => {
        setEditUser(user);
        setIsOpen(true);
    };

    const filteredUsers = users
        .filter((user) =>
            user.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
            user.email.toLowerCase().includes(searchQuery.toLowerCase())
        )
        .filter((user) => selectedRole === "Semua" || user.role === selectedRole);

    return (
        <div className="p-6">
            <div className="flex flex-col md:flex-row justify-between items-center mb-4">
                <h1 className="text-2xl font-bold mb-4 md:mb-0">Manajemen Pengguna</h1>
                <div className="flex flex-col md:flex-row space-y-2 md:space-y-0 md:space-x-2">
                    <select
                        className="border p-2 rounded w-full md:w-auto"
                        value={selectedRole}
                        onChange={(e) => setSelectedRole(e.target.value)}
                    >
                        <option value="Semua">Semua</option>
                        <option value="admin">Admin</option>
                        <option value="doctor">Doctor</option>
                        <option value="nurse">Nurse</option>
                    </select>
                    <div className="relative w-full md:w-auto">
                        <input
                            type="text"
                            placeholder="Cari nama atau email"
                            className="border p-2 pl-8 rounded w-full"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                        />
                        <IconSearch className="absolute left-2 top-2 text-gray-500" size={16} />
                    </div>
                </div>
            </div>
            {loading ? (
                <div className="flex justify-center py-6">
                    <div className="loader border-t-4 border-blue-500 w-8 h-8 rounded-full animate-spin"></div>
                </div>
            ) : (
                <div className="overflow-x-auto">
                    <table className="w-full border-collapse border border-gray-300">
                        <thead>
                        <tr className="bg-gray-200">
                            <th className="border p-2">No</th>
                            <th className="border p-2">Nama</th>
                            <th className="border p-2">Email</th>
                            <th className="border p-2">Role</th>
                            <th className="border p-2">Created</th>
                            <th className="border p-2">Action</th>
                        </tr>
                        </thead>
                        <tbody>
                        {filteredUsers.map((user) => (
                            <tr key={user.id} className="hover:bg-gray-100">
                                <td className="border p-2 text-center">{user.index}</td>
                                <td className="border p-2">{user.name}</td>
                                <td className="border p-2">{user.email}</td>
                                <td className="border p-2">{user.role}</td>
                                <td className="border p-2">{user.createdAt}</td>
                                <td className="border p-2 text-center">
                                    <button
                                        onClick={() => openModal(user)}
                                        className="bg-yellow-500 text-white px-3 py-1 rounded hover:bg-yellow-600 transition mr-2"
                                    >
                                        Edit
                                    </button>
                                    <button
                                        onClick={() => handleDeleteUser(user.id)}
                                        className="bg-red-500 text-white px-3 py-1 rounded hover:bg-red-600 transition"
                                    >
                                        Delete
                                    </button>
                                </td>
                            </tr>
                        ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Modal Edit User */}
            <Transition appear show={isOpen} as={Fragment}>
                <Dialog as="div" className="fixed inset-0 z-50 flex items-center justify-center" onClose={() => setIsOpen(false)}>
                    <Transition.Child
                        as={Fragment}
                        enter="ease-out duration-300"
                        enterFrom="opacity-0"
                        enterTo="opacity-100"
                        leave="ease-in duration-200"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                    >
                        <div className="fixed inset-0 bg-black bg-opacity-50" />
                    </Transition.Child>
                    <div className="fixed inset-0 overflow-y-auto">
                        <div className="flex min-h-full items-center justify-center p-4 text-center">
                            <Dialog.Panel className="w-full max-w-md bg-white dark:bg-gray-800 p-6 rounded shadow-lg">
                                <Dialog.Title className="text-lg font-bold dark:text-gray-200">Edit User</Dialog.Title>
                                <select className="border p-2 rounded w-full mt-4 bg-white dark:bg-gray-700 dark:text-gray-200" value={editUser?.role || ""} onChange={(e) => setEditUser({ ...editUser, role: e.target.value })}>
                                    <option value="admin">Admin</option>
                                    <option value="doctor">Doctor</option>
                                    <option value="nurse">Nurse</option>
                                </select>
                                <button onClick={handleEditRole} className="mt-4 bg-blue-500 text-white px-4 py-2 rounded">Simpan</button>
                            </Dialog.Panel>
                        </div>
                    </div>

                </Dialog>
            </Transition>
        </div>
    );
};

export default UserManagement;